import { type ComponentPropsWithoutRef, type ElementType } from 'react'
import clsx from 'clsx'
import { text, type TextVariants } from './Text.css'

type TextElement = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'div'

type TextProps<T extends TextElement = 'p'> = {
  /** The HTML element to render */
  as?: T
  /** Additional CSS class names */
  className?: string
} & TextVariants &
  ComponentPropsWithoutRef<T>

/**
 * Map variant to default HTML element.
 * When variant is set but `as` is not, use this element.
 */
const variantToElement: Record<string, TextElement> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  label: 'label',
  bodyLg: 'p',
  body: 'p',
  bodySm: 'p',
  bodyXs: 'p',
  caption: 'span',
}

/**
 * A polymorphic text component that can render as any text element.
 *
 * @example
 * ```tsx
 * // Headings (h1-h3 use serif font, h4 uses sans)
 * <Text variant="h1">Page Title</Text>
 * <Text variant="h4">Section Header</Text>
 *
 * // Body text variants
 * <Text variant="body">Default paragraph text</Text>
 * <Text variant="bodySm" color="muted">Helper text</Text>
 * <Text variant="bodyLg" weight="bold">Emphasized text</Text>
 *
 * // Override HTML element when needed
 * <Text variant="h1" as="h2">Visually h1, semantically h2</Text>
 * ```
 */
export function Text<T extends TextElement = 'p'>({
  as,
  variant,
  weight,
  color,
  align,
  truncate,
  className,
  ...props
}: TextProps<T>) {
  // Determine the element: explicit `as` > variant default > 'p'
  const defaultElement = variant ? variantToElement[variant] ?? 'p' : 'p'
  const Component = (as ?? defaultElement) as ElementType

  return (
    <Component
      className={clsx(text({ variant, weight, color, align, truncate }), className)}
      {...props}
    />
  )
}
